import { z } from 'zod';
import {
  creerEntierOuNull,
  entierOuNull,
  projetObligatoire,
  texteFacultatif,
} from './champs';

/** Durée en secondes, issue de FormulaireSaisieDuree : jamais négative. */
const dureeOuNull = creerEntierOuNull({
  message: 'saisir_une_duree_positive',
  min: 0,
});

/**
 * Configuration d'un axe. Rien n'y est contrôlé : FormulaireAxe convertit déjà
 * la plage en nombres ou null. looseObject conserve toutes les clés, y compris
 * celles que l'API renverrait sans que le formulaire les connaisse.
 */
const axe = z.looseObject({});

/**
 * Schéma de la charge utile envoyée à la création d'un graphe.
 *
 * FormulaireGraphe sert à la création comme à la modification et construit une
 * charge utile de 22 clés. z.object retirant les clés qu'il ne déclare pas,
 * chacune est déclarée ici, même sans règle : en oublier une la ferait
 * disparaître de la requête.
 *
 * Le nom et le projet sont exigés. Le reste est facultatif, et les règles se
 * limitent à refuser ce qui n'a pas de sens : des lettres dans un nombre, une
 * durée négative, un intervalle dont la fin précède le début.
 */
export const schemaNouveauGraphe = z.object({
  graph_name: z
    .string({ message: 'saisir_un_nom_de_graphe' })
    .trim()
    .min(1, { message: 'saisir_un_nom_de_graphe' }),
  title: texteFacultatif,
  subtitle: texteFacultatif,

  // Un entier, sans liste fermée : les données contiennent un graphe de type
  // 100, absent du glossaire, qu'une liste fermée rendrait impossible à
  // modifier. entierOuNull plutôt que z.coerce, qui changerait null en 0.
  graph_type_id: entierOuNull,

  // Obligatoire : la documentation de l'API ne lui donne pas de valeur par
  // défaut.
  project_id: projetObligatoire,

  private: entierOuNull,
  showgrid: entierOuNull,
  showlegend: entierOuNull,

  time_refresh: dureeOuNull,
  time_period: dureeOuNull,

  // Champ texte où rien n'empêchait jusqu'ici de saisir des lettres, envoyées
  // alors en null sans avertissement.
  measures_num: creerEntierOuNull({
    message: 'saisir_uniquement_des_chiffres',
    min: 0,
  }),

  // Négatif autorisé : selon la documentation de l'API, une valeur négative
  // désigne des périodes futures.
  cumul_num: creerEntierOuNull({ message: 'saisir_un_nombre_entier' }),

  // Listes déroulantes : pas de liste fermée, qui bloquerait sans message
  // visible un graphe dont l'API renverrait une valeur que la liste ne
  // propose pas.
  cumul_interval: texteFacultatif,
  barmode: texteFacultatif,
  time_zone: texteFacultatif,

  select_time: z.array(z.any(), { message: 'valeur_invalide' }).nullish(),

  // Début et fin en secondes UNIX, chacun facultatif. Quand les deux sont
  // renseignés, un intervalle inversé ne désigne aucune période.
  time_interval: z
    .tuple([entierOuNull, entierOuNull], { message: 'valeur_invalide' })
    .refine(([debut, fin]) => debut === null || fin === null || debut < fin, {
      message: 'date_de_fin_avant_date_de_debut',
    }),

  xaxis: axe,
  yaxis: axe,
  yaxis2: axe,

  curves_conf_list: z.array(z.any(), { message: 'valeur_invalide' }),
  list_template_id: z.array(z.any(), { message: 'valeur_invalide' }),
});

/**
 * Schéma de la charge utile envoyée à la modification d'un graphe.
 * Même contrat, plus l'identifiant du graphe visé.
 */
export const schemaModificationGraphe = schemaNouveauGraphe.extend({
  graph_id: z.coerce.number({ message: 'valeur_invalide' }).int(),
});
